import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axiosClient from '../../../components/api/axiosClient';
import LoadContentSpinner from '../../../components/Utils/LoadContentSpinner';
import { formatPrice } from '../../../components/Utils/UtilsPricesFormat';
import { createPaymentLink } from './paymentLinksStore';
import PassSessionTabs from '../../../components/Dashboard/PassSessionTabs';
import SessionBadge from '../../../components/Dashboard/SessionBadge';
import {
    isMultiSessionEvent,
    resolveInitialTicketTab,
    ticketSessionLabel,
    ticketsForTab,
} from '../../../utils/sessionTickets';

const STEPS = [
    { id: 1, label: 'Quoi' },
    { id: 2, label: 'Pour qui' },
    { id: 3, label: 'Contrôle' },
];

const MIN_CUSTOM_AMOUNT = 1;

function defaultExpiryIso() {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    d.setMinutes(0, 0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function PaymentLinkCreate() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const prefilledEventId = searchParams.get('event') || '';

    const [step, setStep] = useState(1);
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [loadingTickets, setLoadingTickets] = useState(false);
    const [events, setEvents] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [createdLink, setCreatedLink] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [copied, setCopied] = useState(false);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [eventDetail, setEventDetail] = useState(null);
    const [activeTicketTab, setActiveTicketTab] = useState(null);

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(''), 5000);
    };

    const [form, setForm] = useState({
        event_id: prefilledEventId,
        pricing_mode: 'pass', // 'pass' | 'custom'
        ticket_id: '',
        quantity: 1,
        custom_amount: '',
        custom_label: '',
        recipient_name: '',
        recipient_phone: '',
        recipient_email: '',
        note: '',
        expires_at: defaultExpiryIso(),
        max_uses: 1,
    });

    useEffect(() => {
        document.title = 'Nouveau lien de paiement - Events';
        setLoadingEvents(true);
        axiosClient
            .get('/dashboard/promotions/events')
            .then((response) => {
                if (response.data.noContent) {
                    setEvents([]);
                } else {
                    setEvents(response.data.data || []);
                }
            })
            .catch(() => setEvents([]))
            .finally(() => setLoadingEvents(false));
    }, []);

    useEffect(() => {
        if (!form.event_id) {
            setTickets([]);
            return;
        }
        setLoadingTickets(true);
        axiosClient
            .get(`/dashboard/promotions/events/${form.event_id}`)
            .then((response) => {
                const data = response.data?.data || {};
                setEventDetail(data);
                const list = (data.tickets || []).map((t) => {
                    const catalog = Number(t.prix_unitaire ?? t.catalog_price ?? t.price ?? 0) || 0;
                    const promoActive = t.promotion?.is_currently_active;
                    const promoPrice = Number(t.promotion?.promo_price);
                    const price =
                        promoActive && Number.isFinite(promoPrice) && promoPrice > 0
                            ? promoPrice
                            : catalog;
                    return {
                        id: t.pivot_id || t.id,
                        title: t.name || t.title || t.label || 'Billet',
                        price,
                        catalog_price: catalog,
                        has_promo: Boolean(promoActive && promoPrice < catalog),
                        validity_type: t.validity_type,
                        event_session_id: t.event_session_id,
                        session_label: t.session_label,
                        session_date: t.session_date,
                        session_starts_at: t.session_starts_at,
                        session_ends_at: t.session_ends_at,
                    };
                });
                setTickets(list);
                if (isMultiSessionEvent(data)) {
                    setActiveTicketTab(resolveInitialTicketTab(data, list));
                } else {
                    setActiveTicketTab(null);
                }
            })
            .catch(() => setTickets([]))
            .finally(() => setLoadingTickets(false));
    }, [form.event_id]);

    const selectedEvent = useMemo(
        () => events.find((e) => String(e.id) === String(form.event_id)) || null,
        [events, form.event_id],
    );

    const selectedTicket = useMemo(
        () => tickets.find((t) => String(t.id) === String(form.ticket_id)) || null,
        [tickets, form.ticket_id],
    );

    const isMultiSession = isMultiSessionEvent(eventDetail);

    const visibleTickets = useMemo(() => {
        if (!isMultiSession || !activeTicketTab) return tickets;
        return ticketsForTab(tickets, activeTicketTab, eventDetail);
    }, [tickets, isMultiSession, activeTicketTab, eventDetail]);

    const isCustomMode = form.pricing_mode === 'custom';
    const customAmount = Number(String(form.custom_amount).replace(/\s/g, '').replace(',', '.')) || 0;
    const unitPrice = isCustomMode
        ? customAmount
        : Number(selectedTicket?.price ?? 0) || 0;
    const quantity = isCustomMode ? 1 : Number(form.quantity) || 0;
    const totalAmount = unitPrice * quantity;
    const lineLabel = isCustomMode
        ? form.custom_label.trim() || 'Montant personnalisé'
        : selectedTicket?.title || 'Billet';

    const setField = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const setPricingMode = (mode) => {
        setForm((prev) => ({
            ...prev,
            pricing_mode: mode,
            // Reset the other branch to avoid stale validation
            ticket_id: mode === 'pass' ? prev.ticket_id : '',
            quantity: mode === 'pass' ? prev.quantity || 1 : 1,
            custom_amount: mode === 'custom' ? prev.custom_amount : '',
        }));
        setErrors((prev) => ({
            ...prev,
            ticket_id: undefined,
            quantity: undefined,
            custom_amount: undefined,
            pricing_mode: undefined,
        }));
    };

    const validateStep = (current) => {
        const next = {};
        if (current === 1) {
            if (!form.event_id) next.event_id = 'Choisissez un événement.';
            if (form.pricing_mode !== 'pass' && form.pricing_mode !== 'custom') {
                next.pricing_mode = 'Choisissez un mode de tarif.';
            }
            if (form.pricing_mode === 'pass') {
                if (!form.ticket_id) next.ticket_id = 'Choisissez un type de billet.';
                if (!form.quantity || Number(form.quantity) < 1) {
                    next.quantity = 'Quantité minimale : 1.';
                }
            }
            if (form.pricing_mode === 'custom') {
                if (!form.custom_amount || customAmount <= 0) {
                    next.custom_amount = 'Indiquez un montant à faire payer.';
                } else if (customAmount < MIN_CUSTOM_AMOUNT) {
                    next.custom_amount = `Montant minimum : ${MIN_CUSTOM_AMOUNT} XOF.`;
                }
            }
        }
        if (current === 3) {
            if (!form.expires_at) next.expires_at = 'Indiquez une date d’expiration.';
            else if (new Date(form.expires_at).getTime() <= Date.now()) {
                next.expires_at = 'La date doit être dans le futur.';
            }
            if (![1, 3, 5, 10].includes(Number(form.max_uses))) {
                next.max_uses = 'Choisissez un nombre d’usages valide.';
            }
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const goNext = () => {
        if (!validateStep(step)) return;
        setStep((s) => Math.min(3, s + 1));
    };

    const goPrev = () => setStep((s) => Math.max(1, s - 1));

    const handleCreate = async () => {
        if (!validateStep(3) || submitting) return;

        setSubmitting(true);
        setErrors((prev) => ({ ...prev, form: undefined }));
        try {
            const link = await createPaymentLink({
                event_id: form.event_id,
                pricing_mode: form.pricing_mode,
                ticket_id: isCustomMode ? null : form.ticket_id,
                quantity: quantity || 1,
                custom_amount: isCustomMode ? customAmount : null,
                custom_label: isCustomMode ? form.custom_label.trim() : '',
                recipient_name: form.recipient_name.trim(),
                recipient_phone: form.recipient_phone.trim(),
                recipient_email: form.recipient_email.trim(),
                note: form.note.trim(),
                expires_at: new Date(form.expires_at).toISOString(),
                max_uses: Number(form.max_uses) || 1,
            });
            setCreatedLink(link);
            showSuccess('Lien de paiement créé.');
        } catch (err) {
            const apiErrors = err?.response?.data?.errors;
            if (apiErrors && typeof apiErrors === 'object') {
                const next = {};
                Object.entries(apiErrors).forEach(([key, messages]) => {
                    next[key] = Array.isArray(messages) ? messages[0] : String(messages);
                });
                setErrors(next);
            } else {
                setErrors({
                    form:
                        err?.response?.data?.message ||
                        'Impossible de créer le lien de paiement. Réessayez.',
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopy = async () => {
        if (!createdLink?.public_url) return;
        try {
            await navigator.clipboard.writeText(createdLink.public_url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch {
            // ignore
        }
    };

    const waShareUrl = createdLink
        ? `https://wa.me/?text=${encodeURIComponent(
              `${createdLink.recipient_name ? `Bonjour ${createdLink.recipient_name},\n\n` : ''}Voici votre lien de paiement pour ${createdLink.event_title} (${
                  createdLink.pricing_mode === 'custom'
                      ? createdLink.ticket_title
                      : `${createdLink.ticket_title} × ${createdLink.quantity}`
              }) :\n${createdLink.public_url}`,
          )}`
        : '#';

    if (loadingEvents) {
        return <LoadContentSpinner />;
    }

    if (createdLink) {
        return (
            <div className="ch3yp cnbwt cs7xl clu2m c6btv clbq0 cxsfz max-w-2xl mx-auto">
                {successMessage && (
                    <div className="flex w-full justify-center items-center relative">
                        <div className="text-sm fixed mt-10 z-[200] px-3 rounded-lg bg-green-100 text-gray-700 shadow-md">
                            <div className="flex items-center gap-2 py-2">
                                <svg className="text-green-500 shrink-0" width={16} height={16} viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zM7 11.4L3.6 8 5 6.6l2 2 4-4L12.4 6 7 11.4z" />
                                </svg>
                                <div>{successMessage}</div>
                            </div>
                        </div>
                    </div>
                )}
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 sm:p-8 text-center animate-[fadeInUp_0.4s_ease-out]">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                        Lien prêt à envoyer
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                        {createdLink.recipient_name
                            ? `Pour ${createdLink.recipient_name} · `
                            : ''}
                        {createdLink.pricing_mode === 'custom'
                            ? createdLink.ticket_title
                            : `${createdLink.ticket_title} × ${createdLink.quantity}`}
                        {' · '}
                        {formatPrice(
                            (Number(createdLink.unit_price) || 0) *
                                (Number(createdLink.quantity) || 0),
                        )}
                    </p>

                    <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 px-4 py-3 text-left mb-4">
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-400 mb-1">
                            Lien public
                        </p>
                        <p className="text-sm break-all text-gray-800 dark:text-gray-100 font-medium">
                            {createdLink.public_url}
                        </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-2 mb-6">
                        <button
                            type="button"
                            onClick={handleCopy}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700"
                        >
                            {copied ? 'Copié' : 'Copier le lien'}
                        </button>
                        <a
                            href={waShareUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            WhatsApp
                        </a>
                        <Link
                            to={`/dashboard/communiquer/liens-paiement/${createdLink.id}`}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            Voir le détail
                        </Link>
                    </div>

                    <Link
                        to="/dashboard/communiquer/liens-paiement"
                        className="text-sm font-medium text-primary-700 dark:text-primary-300 hover:underline"
                    >
                        Retour à la liste
                    </Link>
                </div>
                <style>{`
                    @keyframes fadeInUp {
                        from { opacity: 0; transform: translateY(8px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="ch3yp cnbwt cs7xl clu2m c6btv clbq0 cxsfz max-w-3xl mx-auto">
            <div className="mb-6">
                <Link
                    to="/dashboard/communiquer/liens-paiement"
                    className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-700 dark:hover:text-primary-300 mb-3"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                    Liens de paiement
                </Link>
                <h1 className="text-gray-800 dark:text-gray-100 font-bold text-2xl tracking-tight">
                    Nouveau lien de paiement
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Préparez une réservation directe pour un acheteur ciblé.
                </p>
            </div>

            {/* Steps */}
            <ol className="flex items-center gap-2 mb-8">
                {STEPS.map((s, idx) => {
                    const active = step === s.id;
                    const done = step > s.id;
                    return (
                        <li key={s.id} className="flex items-center gap-2 flex-1 min-w-0">
                            <div
                                className={`flex items-center gap-2 min-w-0 ${
                                    active
                                        ? 'text-primary-700 dark:text-primary-300'
                                        : done
                                          ? 'text-emerald-700 dark:text-emerald-300'
                                          : 'text-gray-400'
                                }`}
                            >
                                <span
                                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                                        active
                                            ? 'bg-primary-600 text-white'
                                            : done
                                              ? 'bg-emerald-500 text-white'
                                              : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                                    }`}
                                >
                                    {done ? '✓' : s.id}
                                </span>
                                <span className="text-sm font-semibold truncate hidden sm:inline">
                                    {s.label}
                                </span>
                            </div>
                            {idx < STEPS.length - 1 && (
                                <div
                                    className={`h-px flex-1 ${
                                        done ? 'bg-emerald-400' : 'bg-gray-200 dark:bg-gray-700'
                                    }`}
                                />
                            )}
                        </li>
                    );
                })}
            </ol>

            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 sm:p-6">
                {step === 1 && (
                    <div className="space-y-5 animate-[fadeIn_0.3s_ease-out]">
                        <div>
                            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">
                                Quoi faire payer ?
                            </h2>
                            <p className="text-sm text-gray-500">
                                Choisissez l’événement, puis un pass catalogue ou un montant libre.
                            </p>
                        </div>

                        {events.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-4 py-8 text-center text-sm text-gray-500">
                                Aucun événement publié disponible.{' '}
                                <Link to="/dashboard/events/show-list" className="text-primary-700 underline">
                                    Voir mes événements
                                </Link>
                            </div>
                        ) : (
                            <>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                                        Événement
                                    </label>
                                    <select
                                        value={form.event_id}
                                        onChange={(e) => {
                                            setField('event_id', e.target.value);
                                            setField('ticket_id', '');
                                        }}
                                        className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm"
                                    >
                                        <option value="">Sélectionner…</option>
                                        {events.map((ev) => (
                                            <option key={ev.id} value={ev.id}>
                                                {ev.name || ev.event_title}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.event_id && (
                                        <p className="text-xs text-red-600 mt-1">{errors.event_id}</p>
                                    )}
                                </div>

                                {form.event_id && (
                                    <div>
                                        <p className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                                            Mode de tarif
                                        </p>
                                        <div className="grid sm:grid-cols-2 gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setPricingMode('pass')}
                                                className={`text-left rounded-xl border px-4 py-3 transition-colors ${
                                                    form.pricing_mode === 'pass'
                                                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10'
                                                        : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-900/40'
                                                }`}
                                            >
                                                <p className="font-semibold text-gray-800 dark:text-gray-100 text-sm">
                                                    Pass de l’événement
                                                </p>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    VIP, Standard, Salon…
                                                </p>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setPricingMode('custom')}
                                                className={`text-left rounded-xl border px-4 py-3 transition-colors ${
                                                    form.pricing_mode === 'custom'
                                                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10'
                                                        : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-900/40'
                                                }`}
                                            >
                                                <p className="font-semibold text-gray-800 dark:text-gray-100 text-sm">
                                                    Montant libre
                                                </p>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    Saisir le prix à encaisser
                                                </p>
                                            </button>
                                        </div>
                                        {errors.pricing_mode && (
                                            <p className="text-xs text-red-600 mt-1">{errors.pricing_mode}</p>
                                        )}
                                    </div>
                                )}

                                {form.event_id && form.pricing_mode === 'pass' && (
                                    <>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                                                Type de billet
                                            </label>
                                            {loadingTickets ? (
                                                <p className="text-sm text-gray-400">Chargement des billets…</p>
                                            ) : tickets.length === 0 ? (
                                                <p className="text-sm text-amber-700 dark:text-amber-300">
                                                    Aucun billet disponible. Utilisez plutôt un montant libre.
                                                </p>
                                            ) : (
                                                <>
                                                    {isMultiSession && (
                                                        <PassSessionTabs
                                                            event={eventDetail}
                                                            tickets={tickets}
                                                            activeTab={activeTicketTab}
                                                            onChange={setActiveTicketTab}
                                                            className="mb-3"
                                                        />
                                                    )}
                                                <div className="grid gap-2">
                                                    {visibleTickets.map((t) => {
                                                        const price = Number(t.price ?? t.prix_unitaire ?? 0) || 0;
                                                        const selected = String(form.ticket_id) === String(t.id);
                                                        const sessionHint = ticketSessionLabel(t);
                                                        return (
                                                            <button
                                                                key={t.id}
                                                                type="button"
                                                                onClick={() => setField('ticket_id', t.id)}
                                                                className={`text-left rounded-xl border px-4 py-3 transition-colors ${
                                                                    selected
                                                                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10'
                                                                        : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-900/40'
                                                                }`}
                                                            >
                                                                <div className="flex items-center justify-between gap-3">
                                                                    <span className="font-semibold text-gray-800 dark:text-gray-100">
                                                                        {t.title}
                                                                        {t.has_promo && (
                                                                            <span className="ml-2 text-[10px] font-bold uppercase text-rose-600">
                                                                                Promo
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                    <span className="text-sm font-bold tabular-nums text-gray-700 dark:text-gray-200">
                                                                        {formatPrice(price)}
                                                                    </span>
                                                                </div>
                                                                {sessionHint && (
                                                                    <div className="mt-1.5">
                                                                        <SessionBadge ticket={t} />
                                                                    </div>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                                </>
                                            )}
                                            {errors.ticket_id && (
                                                <p className="text-xs text-red-600 mt-1">{errors.ticket_id}</p>
                                            )}
                                        </div>

                                        <div className="max-w-[10rem]">
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                                                Quantité
                                            </label>
                                            <input
                                                type="number"
                                                min={1}
                                                max={50}
                                                value={form.quantity}
                                                onChange={(e) => setField('quantity', e.target.value)}
                                                className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm"
                                            />
                                            {errors.quantity && (
                                                <p className="text-xs text-red-600 mt-1">{errors.quantity}</p>
                                            )}
                                        </div>
                                    </>
                                )}

                                {form.event_id && form.pricing_mode === 'custom' && (
                                    <div className="space-y-4 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                                                Montant à faire payer (XOF)
                                            </label>
                                            <input
                                                type="number"
                                                min={MIN_CUSTOM_AMOUNT}
                                                step={100}
                                                value={form.custom_amount}
                                                onChange={(e) => setField('custom_amount', e.target.value)}
                                                placeholder="Ex. 25000"
                                                className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm"
                                            />
                                            {errors.custom_amount && (
                                                <p className="text-xs text-red-600 mt-1">{errors.custom_amount}</p>
                                            )}
                                            {customAmount > 0 && !errors.custom_amount && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Aperçu : {formatPrice(customAmount)}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                                                Libellé (optionnel)
                                            </label>
                                            <input
                                                type="text"
                                                value={form.custom_label}
                                                onChange={(e) => setField('custom_label', e.target.value)}
                                                placeholder="Ex. Salon VIP table 4, Acompte, Pack hôte…"
                                                className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                Visible pour vous et sur le résumé acheteur.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-5 animate-[fadeIn_0.3s_ease-out]">
                        <div>
                            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">
                                Pour qui ?
                            </h2>
                            <p className="text-sm text-gray-500">
                                Optionnel — utile pour le suivi et le message de partage.
                            </p>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                                    Nom du destinataire
                                </label>
                                <input
                                    type="text"
                                    value={form.recipient_name}
                                    onChange={(e) => setField('recipient_name', e.target.value)}
                                    placeholder="Ex. Aya Koné"
                                    className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                                    Téléphone
                                </label>
                                <input
                                    type="tel"
                                    value={form.recipient_phone}
                                    onChange={(e) => setField('recipient_phone', e.target.value)}
                                    placeholder="+225 …"
                                    className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                                    E-mail
                                </label>
                                <input
                                    type="email"
                                    value={form.recipient_email}
                                    onChange={(e) => setField('recipient_email', e.target.value)}
                                    placeholder="optionnel"
                                    className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                                    Note interne
                                </label>
                                <textarea
                                    rows={3}
                                    value={form.note}
                                    onChange={(e) => setField('note', e.target.value)}
                                    placeholder="Ex. Salon VIP table 4 — hôte presse"
                                    className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-5 animate-[fadeIn_0.3s_ease-out]">
                        <div>
                            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">
                                Contrôle du lien
                            </h2>
                            <p className="text-sm text-gray-500">
                                Expiration, usages, et aperçu avant envoi.
                            </p>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                                    Expire le
                                </label>
                                <input
                                    type="datetime-local"
                                    value={form.expires_at}
                                    onChange={(e) => setField('expires_at', e.target.value)}
                                    className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm"
                                />
                                {errors.expires_at && (
                                    <p className="text-xs text-red-600 mt-1">{errors.expires_at}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5">
                                    Nombre d’usages
                                </label>
                                <select
                                    value={form.max_uses}
                                    onChange={(e) => setField('max_uses', Number(e.target.value))}
                                    className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm"
                                >
                                    <option value={1}>1 utilisation (recommandé)</option>
                                    <option value={3}>3 utilisations</option>
                                    <option value={5}>5 utilisations</option>
                                    <option value={10}>10 utilisations</option>
                                </select>
                                {errors.max_uses && (
                                    <p className="text-xs text-red-600 mt-1">{errors.max_uses}</p>
                                )}
                            </div>
                        </div>

                        <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 p-4">
                            <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-400 mb-3">
                                Aperçu acheteur
                            </p>
                            <div className="space-y-1.5 text-sm">
                                <p className="font-bold text-gray-800 dark:text-gray-100 text-base">
                                    {selectedEvent?.name || selectedEvent?.event_title || '—'}
                                </p>
                                <p className="text-gray-600 dark:text-gray-300">
                                    {isCustomMode
                                        ? lineLabel
                                        : `${selectedTicket?.title || '—'} × ${form.quantity}`}
                                </p>
                                <p className="text-xs text-gray-400">
                                    {isCustomMode ? 'Montant libre' : 'Pass catalogue'}
                                </p>
                                {form.recipient_name && (
                                    <p className="text-gray-500">Pour {form.recipient_name}</p>
                                )}
                                <p className="pt-2 text-lg font-bold text-primary-700 dark:text-primary-300">
                                    {formatPrice(totalAmount)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {errors.form && (
                    <p className="mt-4 text-sm text-rose-600 dark:text-rose-400">{errors.form}</p>
                )}

                <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 dark:border-gray-700 pt-5">
                    <button
                        type="button"
                        onClick={step === 1 ? () => navigate('/dashboard/communiquer/liens-paiement') : goPrev}
                        disabled={submitting}
                        className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                    >
                        {step === 1 ? 'Annuler' : 'Retour'}
                    </button>
                    {step < 3 ? (
                        <button
                            type="button"
                            onClick={goNext}
                            disabled={events.length === 0 && step === 1}
                            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                        >
                            Continuer
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={handleCreate}
                            disabled={submitting}
                            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                        >
                            {submitting ? 'Création…' : 'Générer le lien'}
                        </button>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
            `}</style>
        </div>
    );
}
